// Polls the same /api/zones endpoint tv.js already uses (see
// kiosk-app/public/js/tv.js pickTvZoneState), filtered to a single room,
// and normalizes the raw Sonos state into a stable "visual profile" the
// visualizers can consume without knowing anything about Sonos's JSON
// shape. No WebSocket involved - this project doesn't have one (see the
// party-mode planning discussion); polling is what tv.html already does
// in production, so this reuses that exact same approach.

import { API_BASE } from './api-base.js';
import { buildAnalysisTimeline } from './track-analysis.js';

const POLL_MS = 3000;


// djb2 - deterministic, stable across reloads/sessions, good enough for a
// visual seed (not a security hash). Returns a hex string.
function hashString(str) {
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) + hash + str.charCodeAt(i)) | 0;
  }
  return (hash >>> 0).toString(16);
}

// Same priority-list logic as tv.js's pickTvZoneState: only react to
// rooms actually meant for this screen (e.g. 'Play'/'Living Room'), not
// to any speaker in the house - a track playing in the Bedroom shouldn't
// make the living-room TV's party mode light up. Falls through the list
// in order and returns the first one actually PLAYING.
//
// Matches against every member of a zone group, not just its
// coordinator - Sonos elects ONE speaker per group as coordinator.
// roomName (arbitrarily, whichever one happens to hold that role right
// now), so a grouped room can be actively playing without ever showing
// up as z.coordinator.roomName. Concretely: grouping Living Room under
// Bedroom (Bedroom elected coordinator) made this screen show "nothing
// playing" while Living Room's own speaker was audibly playing - matching
// coordinator-only missed it entirely. The whole group shares one
// playbackState/currentTrack regardless of which member is playing it,
// so zone.coordinator.state is still the right state to return even
// when the match came from a member.
function zoneMatchesRoom(zone, roomName) {
  if (zone.coordinator && zone.coordinator.roomName === roomName) return true;
  return Array.isArray(zone.members) && zone.members.some(function (m) { return m.roomName === roomName; });
}

function pickZoneState(zones, allowedRooms) {
  for (let i = 0; i < allowedRooms.length; i++) {
    const zone = zones.find(function (z) { return zoneMatchesRoom(z, allowedRooms[i]); });
    if (zone && zone.coordinator.state && zone.coordinator.state.playbackState === 'PLAYING') {
      return { state: zone.coordinator.state, roomName: allowedRooms[i] };
    }
  }
  return null;
}

// How the audio actually reaches the speaker, derived from the track URI.
// This matters for one reason: WHEN Sonos says a track is, relative to when
// you HEAR it, is not the same for every source.
//
//   'direct'  - Sonos fetches and decodes the stream itself
//               (x-sonos-spotify:, x-sonos-http:, x-file-cifs:, ...).
//   'stream'  - something else is pushing audio INTO the speaker: AirPlay and
//               Spotify Connect arrive as a "virtual line-in" (x-sonos-vli:),
//               a physical input as x-rincon-stream:. These buffer, so the
//               position Sonos reports runs AHEAD of what comes out of the
//               speaker.
//
// Anything unrecognised is treated as 'direct', which is the no-correction
// case - an unknown source should not silently acquire an offset.
//
// NOTE: the x-sonos-vli: prefix is documented and is what Sonos uses for
// AirPlay, but I could not observe a live AirPlay payload from here to
// confirm it on THIS system - the readout in party-mode's bottom bar prints
// the detected source for exactly that reason, so a wrong guess is visible
// rather than silent.
function detectSource(uri) {
  if (typeof uri !== 'string' || uri === '') return 'direct';
  // The /radio page (soloist-visual-profile.js) - its position is already
  // corrected for the Icecast stream's own latency, and neither Sonos
  // correction applies. No entry in LYRICS_OFFSET_BY_SOURCE, so offset 0.
  if (uri.indexOf('x-piradio:') === 0) return 'radio';
  if (uri.indexOf('x-sonos-vli:') === 0 || uri.indexOf('x-rincon-stream:') === 0) return 'stream';
  if (uri.toLowerCase().indexOf('airplay') !== -1) return 'stream';
  return 'direct';
}

// How the payloads become the single number the orchestrator pools on. The
// precedence is SET here, not derived, and it is deliberately not radiopi's
// own:
//
//   BOTH llmValue and a real   -> the AVERAGE of the two. Explicit request
//   measurement (source          after max() ran for a while: max leaned
//   'signal') present            everything danceable, the mean keeps both
//                                opinions in the number.
//   else whichever ONE exists -> use it, whatever its confidence
//   else previous track       -> whatever the LAST track actually resolved to
//   else                      -> neutral 0.5
//
// Two consequences worth being explicit about, both intended:
//
//   The model still stands in for radiopi's own refusal. When the model is
//   unsure of a track, radiopi puts its estimate in `llmValue` and
//   deliberately keeps 0.5 in `value` with a non-'signal' source; that 0.5
//   is not a measurement and never enters the average.
//
//   DEEZER IS OUT OF THE CHAIN ENTIRELY (also by explicit request - it used
//   to be the last resort before the carry). It is a different measurement
//   of a different thing on a different scale, and its figures ran inflated
//   enough to pin whole playlists into the high pool. trackFeatures keeps
//   carrying it for the debug bar's display, but it never reaches the
//   resolved index.
//
// Never null: an index always exists, so the pool is restricted from the first
// frame rather than being unrestricted and then narrowing a moment later.
export const NEUTRAL_DANCEABILITY = 0.5;

// The last index that came from an actual SOURCE, carried across track changes.
//
// A track change wipes the profile, so for the first seconds of every new track
// nothing has resolved yet and the chain used to fall straight to 0.5. That is
// not a neutral thing to do: 0.5 is a real position on the axis, and the pool
// treats it as a measurement, so every gap between tracks pulled the whole
// library selection to the middle of the scale. Carrying the previous track's
// index is the better guess by far - listening runs in stretches of one style,
// so the next track resembles the last one far more often than it resembles the
// midpoint of everything.
//
// ONLY MEASURED VALUES ARE REMEMBERED. The neutral fallback and this carry
// itself are deliberately never written here: if a guess could be promoted into
// the memory, one data-less track would pin the index for the rest of the
// session and this would be 0.5 wearing a different hat.
let lastResolvedDanceability = null;

export function resolveDanceabilityIndex(profile) {
  const d = profile && profile.danceability;
  // LE SERVEUR A DÉJÀ TRANCHÉ : on ne refait pas le choix ici.
  //
  // Cette fonction faisait la MOYENNE de l'estimation du modèle et de la
  // mesure du signal. C'était une seconde moyenne, invisible, qui s'ajoutait
  // à celle que le service appliquait déjà - et elle ramenait tout vers le
  // milieu : un morceau noté 0.70 par le modèle et 0.30 par le signal
  // ressortait à 0.50, c'est-à-dire exactement le neutre, sur un titre dont
  // on savait pourtant quelque chose.
  //
  // Depuis le 18/09/2026, le service décide seul - le modèle l'emporte, la
  // mesure sert de repli - et `value` porte sa décision. `source: 'default'`
  // reste le seul cas où il dit n'avoir rien su : celui-là ne doit pas être
  // mémorisé, au même titre que le neutre (voir le commentaire ci-dessus).
  const decided = d && typeof d.value === 'number' && d.source !== 'default'
    ? d.value
    : null;
  if (decided != null) {
    lastResolvedDanceability = decided;
    return decided;
  }
  // DEUX ABSENCES DIFFÉRENTES, QU'IL NE FAUT PAS CONFONDRE.
  //
  // « Le service a répondu qu'il ne sait pas » (source 'default') est une
  // RÉPONSE : ni le modèle ni la mesure n'ont rien produit, et le milieu de
  // l'échelle est alors la bonne valeur. Reporter celle du morceau précédent
  // reviendrait à faire tourner un titre inconnu sur le vivier du titre
  // d'avant - d'autant plus voyant depuis que le vivier ne pioche plus que
  // vers le haut : un morceau calme hériterait du vivier de danse.
  if (d && d.source === 'default') return NEUTRAL_DANCEABILITY;
  // « Rien n'est encore arrivé » est autre chose : l'analyse est en vol, une
  // seconde ou deux après le début du morceau. Là, le report vaut mieux qu'un
  // saut au milieu - l'écoute va par plages d'un même style.
  if (lastResolvedDanceability != null) return lastResolvedDanceability;
  // Avant toute résolution, c'est-à-dire au tout premier morceau d'une séance.
  return NEUTRAL_DANCEABILITY;
}

// Test seam: the carry is module state that outlives every profile, so a test
// (and a session restart) needs a way to clear it.
export function resetDanceabilityCarry() {
  lastResolvedDanceability = null;
}

// trackId -> the definitive (source 'signal') index. Module-level on purpose:
// it has to outlive the profile object, which is rebuilt on every poll and
// cleared on every track change - a per-profile cache would be thrown away
// exactly when it becomes useful.
const DANCEABILITY_BY_TRACK = new Map();

function buildProfile(rawState, roomName, previousProfile) {
  const track = (rawState && rawState.currentTrack) || {};
  const title = track.stationName || track.title || '';
  const artist = track.artist || '';
  const album = track.album || '';
  const trackId = hashString(title + '|' + artist + '|' + album);
  const trackChanged = !previousProfile || previousProfile.trackId !== trackId;
  const duration = typeof track.duration === 'number' ? track.duration : 0;
  const position = rawState && typeof rawState.elapsedTime === 'number' ? rawState.elapsedTime : 0;

  return {
    playbackState: rawState ? rawState.playbackState || 'STOPPED' : 'STOPPED',
    // Whichever allowedRoom actually matched this poll, null while idle -
    // lets Left/Right transport commands (see main.js) target the exact
    // room this screen is showing rather than some other default.
    roomName: roomName,
    position: position,
    duration: duration,
    progress: duration > 0 ? Math.min(1, Math.max(0, position / duration)) : 0,
    trackId: trackId,
    title: title,
    artist: artist,
    album: album,
    // 'direct' | 'stream' - see detectSource above. Consumed by main.js's
    // lyrics timing, which needs a different offset for each.
    source: detectSource(track.uri || track.trackUri),
    trackUri: track.uri || track.trackUri || '',
    artworkUrl: track.absoluteAlbumArtUri || null,
    // Carried over unless the track actually changed - ArtworkPalette
    // fills this in asynchronously (see artwork-palette.js) and we don't
    // want to drop a perfectly good palette just because a poll tick
    // came back with the same track.
    // Carried across a TRACK CHANGE too, not just between polls of the same
    // track - which is the opposite of what it used to do, and the change
    // matters. Clearing it on the track change dropped every visualizer back
    // to its hardcoded fallback colours at the instant Sonos announced the new
    // song, i.e. one whole download before the new cover appeared: the scene
    // visibly changed colour while the OLD cover was still on screen, and then
    // changed again when the real palette arrived. Holding the outgoing
    // palette until the crossfade replaces it (scene-manager.js's
    // beginPaletteBlend, started from artwork-transition.js's single event) is
    // what makes the room turn with the picture instead of ahead of it. A
    // track with no cover art at all clears it explicitly at that same
    // instant - see main.js's onArtworkTransitionStart.
    artworkPalette: previousProfile ? previousProfile.artworkPalette : null,
    // Same carry-over-unless-track-changed pattern as artworkPalette -
    // filled in asynchronously by track-features-client.js (Deezer BPM +
    // TheAudioDB energy/danceability/valence/mood) via setTrackFeatures()
    // below, consumed by orchestrator.js. Every field on it is nullable;
    // null here just means "not resolved yet" the same way it does for a
    // genuinely-unmatched track.
    trackFeatures: !trackChanged && previousProfile ? previousProfile.trackFeatures : null,
    // Same carry-over pattern, for the radiopi /analyze full audio
    // analysis (see track-analysis-client.js/track-analysis.js) -
    // consulted first, ahead of trackFeatures, wherever bpm/energy is
    // needed; trackFeatures.bpm still gets set FROM this when it
    // resolves (see main.js), so trackFeatures stays the single source
    // every existing reader already uses.
    trackAnalysis: !trackChanged && previousProfile ? previousProfile.trackAnalysis : null,
    // Precomputed once, at the same time trackAnalysis itself resolves
    // (see setTrackAnalysis below) - buildAnalysisTimeline is the
    // expensive-ish O(n) pass (binary-search-ready beat grid, per-band
    // envelopes), done ONCE per track rather than per frame. null exactly
    // when trackAnalysis is null, or when the analysis didn't carry enough
    // to build a real timeline from (see buildAnalysisTimeline's own
    // comment) - every reader treats null the same "fall back to the
    // synthetic path" way.
    analysisTimeline: !trackChanged && previousProfile ? previousProfile.analysisTimeline : null,
    // Which of radiopi /analyze vs the TheAudioDB fallback is currently
    // resolving/resolved for this track, for the bottom bar's own status
    // segment (see main.js's fmtApiStatusSegment) - one of null (not
    // started yet), 'analyzing', 'analyzed', 'fallback', 'fallback-ok',
    // 'failed'. Reset to null on every track change like the other
    // carry-over fields, set via setApiStatus as main.js's fetch chain
    // progresses.
    apiStatus: !trackChanged && previousProfile ? previousProfile.apiStatus : null,
    // radiopi's danceability index (see track-analysis-client.js). Carried
    // over like the fields above, and deliberately SEPARATE from
    // trackFeatures.danceability, which is Deezer/TheAudioDB's own 0-100
    // figure for a different quantity - showing both in the debug bar is how
    // you tell them apart.
    //
    // Arrives on the FIRST response (a 202 carries the LLM's estimate), then
    // is replaced in place when the audio analysis lands and the same field
    // comes back with source "signal".
    // Seeded from the session cache the instant the track changes, so a
    // replayed track never goes back through the provisional estimate.
    danceability: !trackChanged && previousProfile
      ? previousProfile.danceability
      : (DANCEABILITY_BY_TRACK.get(trackId) || null),
    // Same carry-over pattern again, for the lyric-flash background
    // effect (see lyrics-client.js/lyric-hook.js). lyricsResolved
    // distinguishes "still loading" (false) from "lookup finished"
    // (true, whether or not anything usable came out of it) so main.js's
    // footer readout can show "lyrics not found" only once resolution
    // actually completes, not during the brief in-flight window.
    lyricsResolved: !trackChanged && previousProfile ? previousProfile.lyricsResolved : false,
    lyricsFound: !trackChanged && previousProfile ? previousProfile.lyricsFound : false,
    lyricHooks: !trackChanged && previousProfile ? previousProfile.lyricHooks : [],
    // WHICH LRCLIB record the words came from, and what was actually asked for
    // - see lyricsService.js's describeCandidate. Debug readout only; nothing
    // renders from it. Same carry-over-until-the-track-changes rule as the
    // three fields above, so it never describes the previous song.
    lyricsSource: !trackChanged && previousProfile ? previousProfile.lyricsSource : null,
    lyricsQuery: !trackChanged && previousProfile ? previousProfile.lyricsQuery : null,
    trackChangedAt: trackChanged ? Date.now() : (previousProfile ? previousProfile.trackChangedAt : Date.now()),
    // When the COVER actually crossfaded, which is not when the track changed:
    // the image has to be downloaded and decoded first (see
    // artwork-transition.js). Anything that should look simultaneous with the
    // new cover - the light rig's dip above all - keys off this, not off
    // trackChangedAt, which fires while the previous cover is still up.
    // Cleared on a real track change so a stale value can never re-trigger.
    artworkChangedAt: trackChanged ? null : (previousProfile ? previousProfile.artworkChangedAt : null),
    // The queue's next cover, for pre-warming, when the server gives an
    // absolute URL for it. node-sonos-http-api's nextTrack currently carries
    // only a device-relative albumArtUri, so this is usually null - the guard
    // is here so it starts working the day that changes, not so it works now.
    nextArtworkUrl: (rawState && rawState.nextTrack && rawState.nextTrack.absoluteAlbumArtUri) || null
  };
}

// onUpdate(profile) fires on every poll (even when nothing changed - the
// visualizers use deltaTime every frame regardless). onError(err) fires
// on a failed fetch; polling continues on the next tick either way.
export function createSonosVisualProfileSource(allowedRooms, onUpdate, onError) {
  return createVisualProfileSource(function fetchPicked() {
    return fetch(API_BASE + '/api/zones')
      .then(function (res) { return res.json(); })
      .then(function (zones) { return pickZoneState(zones, allowedRooms); });
  }, onUpdate, onError);
}

// The poll loop and the profile setters, independent of WHERE the state
// comes from. fetchPicked() resolves to { state, roomName } in the Sonos
// state shape buildProfile reads (currentTrack/playbackState/elapsedTime),
// or null when nothing is playing - the Soloist source
// (soloist-visual-profile.js) translates its own payload into that shape so
// every profile field, carry-over rule and setter below is shared verbatim.
export function createVisualProfileSource(fetchPicked, onUpdate, onError) {
  let profile = null;
  let timer = null;
  let stopped = false;

  function poll() {
    if (stopped) return;
    Promise.resolve()
      .then(fetchPicked)
      .then(function (picked) {
        profile = buildProfile(picked ? picked.state : null, picked ? picked.roomName : null, profile);
        onUpdate(profile);
      })
      .catch(function (err) {
        if (onError) onError(err);
      })
      .finally(function () {
        if (!stopped) timer = setTimeout(poll, POLL_MS);
      });
  }

  poll();

  return {
    getProfile: function () { return profile; },
    // ArtworkPalette resolves asynchronously (image load + canvas decode
    // take a beat), well after buildProfile already ran for this track -
    // this is how the result gets back into the profile once it's ready.
    // Guarded by trackId so a palette that resolves late for a track
    // that's since changed doesn't get attached to the wrong one.
    // Mirrors setArtworkPalette - see its comment below.
    setArtworkChangedAt: function (trackId, at) {
      if (profile && profile.trackId === trackId) {
        profile.artworkChangedAt = at;
      }
    },
    setArtworkPalette: function (trackId, palette) {
      if (profile && profile.trackId === trackId) {
        profile.artworkPalette = palette;
      }
    },
    // Mirrors setArtworkPalette exactly - see its comment above.
    setTrackFeatures: function (trackId, features) {
      if (profile && profile.trackId === trackId) {
        profile.trackFeatures = features;
      }
    },
    // radiopi's danceability. Called more than once per track on purpose: the
    // estimate lands within a second, the measurement 60-100s later.
    setDanceability: function (trackId, danceability) {
      // Once the index is MEASURED (source 'signal') it is final, so it is
      // kept for the session against the track's own id and never asked for
      // again - a track played a second time is classified on the frame it
      // starts instead of spending its first 60-100s on the provisional
      // estimate. No expiry: the measurement cannot go stale, and the store is
      // one small entry per distinct track heard.
      if (danceability && danceability.source === 'signal') {
        DANCEABILITY_BY_TRACK.set(trackId, danceability);
      }
      if (profile && profile.trackId === trackId) {
        profile.danceability = danceability;
      }
    },
    // Mirrors setArtworkPalette/setTrackFeatures exactly - see the first
    // one's comment above.
    setTrackAnalysis: function (trackId, analysis) {
      if (profile && profile.trackId === trackId) {
        profile.trackAnalysis = analysis;
        profile.analysisTimeline = buildAnalysisTimeline(analysis);
      }
    },
    // Mirrors setArtworkPalette/setTrackFeatures - see the first one's
    // comment above.
    setApiStatus: function (trackId, status) {
      if (profile && profile.trackId === trackId) {
        profile.apiStatus = status;
      }
    },
    // Mirrors setArtworkPalette/setTrackFeatures exactly - see the first
    // one's comment above. hooks is lyric-hook.js's own result array
    // (possibly empty).
    setLyricHooks: function (trackId, found, hooks) {
      if (profile && profile.trackId === trackId) {
        profile.lyricsResolved = true;
        profile.lyricsFound = found;
        profile.lyricHooks = hooks;
      }
    },
    // Set from the lyrics-banner fetch rather than from setLyricHooks, because
    // the hook fetch sits behind LYRIC_FLASH_ENABLED and is currently off -
    // the banner lookup is the one that actually runs on every track.
    setLyricsSource: function (trackId, source, query) {
      if (profile && profile.trackId === trackId) {
        profile.lyricsSource = source || null;
        profile.lyricsQuery = query || null;
      }
    },
    stop: function () {
      stopped = true;
      clearTimeout(timer);
    }
  };
}

export { hashString };
